import PropTypes from 'prop-types';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import {
  Badge,
  Card,
  Heading,
  Inline,
  RadioCard,
  Stack,
  vars,
  Text,
  textVariants,
} from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import { BrandedFare as BrandedFareType } from '@eti/types';
import { constants, price as priceUtil } from '@eti/utils';
import { getTravelerPrices } from '../../../../utils/travelers';
import { getTripDisplayPrice, getIncludedBaggageText } from '../../../result/utils/resultUtils';
import { INCLUDED_PERSONAL_ITEM } from '../../../../constants/includedBaggageConstants';

const { PER_ADULT, PER_TRAVELER, TOTAL } = constants.priceDisplayTypes;

const getCardStyles = (isRecommended) => css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 1.75rem;

  @media ${mediaQueries.small.only} {
    ${isRecommended && `margin-top: 0.5rem;`}
  }
`;

const featureSectionStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const featureListStyles = css`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const notificationStyles = css`
  margin-top: 16px;
`;

const inlineStyles = (isIncluded) => css`
  ${!isIncluded && `color: #737373`};
`;

const badgeStyles = css`
  display: inline-block;
  left: 0;
  margin: 0 auto;
  position: absolute;
  right: 0;
  top: -16px;
  width: min-content;
`;

const featureTextStyles = css`
  width: -webkit-fill-available; /* stylelint-disable */
`;

const checkCircleStyles = css`
  color: #29a331;
`;

const cardSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: none;
`;

const Feature = ({ isIncluded, label }) => {
  return label ? (
    <Inline as="li" className={inlineStyles(isIncluded)} noWrap role="listitem" spacing={8}>
      {isIncluded ? (
        <CheckCircle className={checkCircleStyles} size={20} weight="fill" />
      ) : (
        <XCircle className={inlineStyles(isIncluded)} size={20} weight="thin" />
      )}
      <Text as="p" className={featureTextStyles}>
        {label}
      </Text>
    </Inline>
  ) : null;
};

Feature.propTypes = {
  isIncluded: PropTypes.bool,
  label: PropTypes.string,
};

const resolvePrice = (brandedFareData, tripPriceDisplayType) => {
  const { travelers, travelerPrices } = brandedFareData;
  const groupedTravelerPrices = getTravelerPrices(travelers, travelerPrices);
  return getTripDisplayPrice(tripPriceDisplayType)(groupedTravelerPrices);
};

export const getFormattedPrice = (brandedFareData, tripPriceDisplayType) =>
  priceUtil.formatPrice(resolvePrice(brandedFareData, tripPriceDisplayType));

const getCheckedBaggageFeature = (
  showNotIncludedProducts,
  checkedBaggageSummary,
  brandedFareData,
  t,
) => {
  let component = null;
  if (checkedBaggageSummary && checkedBaggageSummary.baggageStatus === 'INCLUDED_CHECKED_BAGGAGE') {
    const checkedInBaggageName = getIncludedBaggageText(
      checkedBaggageSummary.baggageDetails,
      'IbeClient.CheckInBaggageIncluded.Label',
      t,
    );
    component = (
      <Feature
        isIncluded
        key={`${brandedFareData.id}${checkedInBaggageName}`}
        label={checkedInBaggageName}
      />
    );
  } else if (
    showNotIncludedProducts &&
    checkedBaggageSummary &&
    (checkedBaggageSummary.baggageStatus === 'NO_CHECKED_BAGGAGE' ||
      checkedBaggageSummary.baggageStatus === 'NO_CHECKED_BAGGAGE_AVAILABLE')
  ) {
    const checkedInBaggageName = getIncludedBaggageText(
      { pieces: 1 },
      'BrandedFares.CheckInBaggageNotIncluded.Text',
      t,
    );
    component = (
      <Feature key={`${brandedFareData.id}${checkedInBaggageName}`} label={checkedInBaggageName} />
    );
  }
  return component;
};

const getCabinBaggageFeature = (
  showNotIncludedProducts,
  cabinBaggageTripSummary,
  brandedFareData,
  t,
) => {
  let component = null;
  if (
    cabinBaggageTripSummary &&
    cabinBaggageTripSummary.cabinBaggageStatus === 'INCLUDED_CABIN_BAGGAGE'
  ) {
    const cabinBaggageName = getIncludedBaggageText(
      cabinBaggageTripSummary.cabinBaggageDetails,
      'IbeClient.CabinBaggageIncluded.Label',
      t,
    );
    component = (
      <Feature
        isIncluded
        key={`${brandedFareData.id}${cabinBaggageName}`}
        label={cabinBaggageName}
      />
    );
  } else if (
    showNotIncludedProducts &&
    cabinBaggageTripSummary &&
    (cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE' ||
      cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE_AVAILABLE')
  ) {
    const cabinBaggageName = getIncludedBaggageText(
      { pieces: 1 },
      'BrandedFares.CabinBaggageNotIncluded.Text',
      t,
    );
    component = (
      <Feature key={`${brandedFareData.id}${cabinBaggageName}`} label={cabinBaggageName} />
    );
  }
  return component;
};

const getPersonalAndCabinBaggageInfo = (isPersonalItemIncluded, cabinBaggageTripSummary, t) => {
  let personalAndCabinText = '';
  let isIncluded = true;
  if (
    isPersonalItemIncluded &&
    cabinBaggageTripSummary?.cabinBaggageStatus === 'INCLUDED_CABIN_BAGGAGE'
  ) {
    personalAndCabinText = getIncludedBaggageText(
      cabinBaggageTripSummary?.cabinBaggageDetails,
      'BrandedFares.PersonalItemAndCabinBaggageIncluded.Text',
      t,
    );
  } else if (
    isPersonalItemIncluded &&
    (cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE' ||
      cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE_AVAILABLE')
  ) {
    personalAndCabinText = t('BrandedFares.PersonalItemIncludedButNoCabinBaggage.Text');
  } else if (
    !isPersonalItemIncluded &&
    cabinBaggageTripSummary?.cabinBaggageStatus === 'INCLUDED_CABIN_BAGGAGE'
  ) {
    personalAndCabinText = getIncludedBaggageText(
      cabinBaggageTripSummary?.cabinBaggageDetails,
      'BrandedFares.NoPersonalItemButCabinBaggageIncluded.Text',
      t,
    );
  } else if (
    !isPersonalItemIncluded &&
    (cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE' ||
      cabinBaggageTripSummary.cabinBaggageStatus === 'NO_CABIN_BAGGAGE_AVAILABLE')
  ) {
    personalAndCabinText = t('BrandedFares.NoPersonalItemAndNoCabinBaggageIncluded.Text');
    isIncluded = false;
  }
  return { isIncluded, personalAndCabinText };
};

const getPersonalItemAndCabinBaggageFeature = (
  showNotIncludedProducts,
  isPersonalItemIncluded,
  cabinBaggageTripSummary,
  brandedFareData,
  p,
  t,
) => {
  let component = null;
  const personalAndCabinBaggageShowType = String(
    p('BrandedFares.PersonalAndCabinBaggage.ShowType'),
  ).toUpperCase();
  const personalAndCabinBaggageDefaultShowType = 'SEPARATE';
  const personalAndCabinBaggageShowTypePair = 'PAIR';
  if (personalAndCabinBaggageShowType === personalAndCabinBaggageDefaultShowType) {
    const personalItemText = isPersonalItemIncluded
      ? t('BrandedFares.PersonalBaggageIncluded.Text')
      : t('BrandedFares.PersonalBaggageNotIncluded.Text');
    const cabinBaggageFeature = getCabinBaggageFeature(
      showNotIncludedProducts,
      cabinBaggageTripSummary,
      brandedFareData,
      t,
    );
    component = (
      <>
        <Feature
          isIncluded={Boolean(isPersonalItemIncluded)}
          key={`${brandedFareData.id}${personalItemText}`}
          label={personalItemText}
        />
        {cabinBaggageFeature}
      </>
    );
  } else if (personalAndCabinBaggageShowType === personalAndCabinBaggageShowTypePair) {
    const { isIncluded, personalAndCabinText } = getPersonalAndCabinBaggageInfo(
      isPersonalItemIncluded,
      cabinBaggageTripSummary,
      t,
    );
    component = (
      <Feature
        isIncluded={isIncluded}
        key={`${brandedFareData.id}${personalAndCabinText}`}
        label={personalAndCabinText}
      />
    );
  }
  return component;
};

const getBrandedFareFeature = (showNotIncludedProducts, brandedFareData, feature) => {
  let component = null;
  if (feature.availability === 'INCLUDED') {
    component = (
      <Feature isIncluded key={`${brandedFareData.id}${feature.name}`} label={feature.name} />
    );
  } else if (showNotIncludedProducts) {
    component = <Feature key={`${brandedFareData.id}${feature.name}`} label={feature.name} />;
  }
  return component;
};

const formatBrandedFareName = (fareName) => {
  const fareNameList = fareName.toLowerCase().split(' ');
  const brandedFareName = fareNameList.map((name) => name.charAt(0).toUpperCase() + name.slice(1));
  return brandedFareName.join(' ');
};

const BrandedFare = ({
  brandedFareData,
  recommendedFareKey,
  shouldShowInformationBox,
  ticketingCarrierCodes = [],
}) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const tripPriceText = {
    [PER_ADULT]: t('Result.PricePerAdult.Text'),
    [PER_TRAVELER]: t('Result.PricePerPerson.Text'),
    [TOTAL]: t('Result.TotalPrice.Text'),
  };
  const tripPriceDisplayType = p('BrandedFares.ShowPrice.Type') || PER_ADULT;
  const informationBoxText = t('BrandedFares.BasicOption.Information.Text');
  const checkedBaggageSummary = brandedFareData?.checkedBaggageSummary?.tripSummaries?.[0];
  const cabinBaggageTripSummary =
    brandedFareData?.cabinBaggageTripSummary?.cabinBaggageTravelerDetails?.[0];
  const personalItemTripSummary =
    brandedFareData?.personalItemTripSummary?.personalItemTravelerDetails[0];
  const isPersonalItemIncluded =
    personalItemTripSummary?.personalItemStatus === INCLUDED_PERSONAL_ITEM;
  const carriersNotIncludedProducts = p('BrandedFares.ShowNotIncluded.Carriers') || [];
  const shouldUseCabinBaggageTextUsingAtpco = p('BrandedFares.CabinBaggage.From.Atpco.Enabled');
  const showNotIncludedProducts = carriersNotIncludedProducts.some((item) =>
    ticketingCarrierCodes.includes(item),
  );
  const shouldShowPersonalItem = p('BrandedFares.ShowPersonalBaggage.Enabled');
  const isRecommended = recommendedFareKey === brandedFareData.selectionKey;

  return (
    <RadioCard
      className={getCardStyles(isRecommended)}
      data-testid={`brandedFare-option-${brandedFareData.id}`}
      id={brandedFareData.id}
      label={
        <>
          <Heading as="span" level={3}>
            {formatBrandedFareName(brandedFareData.name)}
          </Heading>
          {isRecommended && (
            <Badge className={badgeStyles} variant="promotional">
              {t('BrandedFares.Recommended.Text')}
            </Badge>
          )}
        </>
      }
      value={brandedFareData.selectionKey}
    >
      <Card.Section className={cardSectionStyles}>
        <Text
          data-testid={`brandedFare-${brandedFareData.id}-price`}
          variant={textVariants._24_EMPHASIZED}
        >
          {getFormattedPrice(brandedFareData, tripPriceDisplayType)}
        </Text>
        <Text as="p">{tripPriceText[tripPriceDisplayType]}</Text>
      </Card.Section>
      <div className={featureSectionStyles}>
        <Stack as="ul" className={featureListStyles}>
          {!shouldUseCabinBaggageTextUsingAtpco &&
            shouldShowPersonalItem &&
            getPersonalItemAndCabinBaggageFeature(
              showNotIncludedProducts,
              isPersonalItemIncluded,
              cabinBaggageTripSummary,
              brandedFareData,
              p,
              t,
            )}

          {!shouldUseCabinBaggageTextUsingAtpco &&
            !shouldShowPersonalItem &&
            getCabinBaggageFeature(
              showNotIncludedProducts,
              cabinBaggageTripSummary,
              brandedFareData,
              t,
            )}

          {getCheckedBaggageFeature(
            showNotIncludedProducts,
            checkedBaggageSummary,
            brandedFareData,
            t,
          )}
          {brandedFareData.features.map((feature) =>
            getBrandedFareFeature(showNotIncludedProducts, brandedFareData, feature),
          )}
        </Stack>
        {shouldShowInformationBox && informationBoxText && (
          <Notification
            className={notificationStyles}
            dataTestId="brandedFares-firstOption-notification"
            variant={variants.INFORMATION}
          >
            {informationBoxText}
          </Notification>
        )}
      </div>
    </RadioCard>
  );
};

BrandedFare.propTypes = {
  brandedFareData: BrandedFareType,
  recommendedFareKey: PropTypes.string,
  shouldShowInformationBox: PropTypes.bool,
  ticketingCarrierCodes: PropTypes.array,
};

export default BrandedFare;
